import React, { useRef } from 'react';
import PropTypes from 'prop-types';

// Utils
import { SortDirection } from 'ka-table/enums';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import useNotification from '../../../utils/hooks/useNotification';
import useModal from '../../../utils/hooks/useModal';
import PermissionsGate, { isGranted } from '../../../utils/gate/permissionsGate';
import SCOPE from '../../../utils/gate/scope';
import { useAppContext } from '../../../store/AppContext';

// Icons
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';

// Services
import useAdministratorsService from '../../../services/administratorsService';

// Components
import GenericTable from '../GenericTable/GenericTable';
import SkeletonLoader from '../../molecules/SkeletonLoader/SkeletonLoader';
import EmptyList from '../../organisms/EmptyList/EmptyList';
import TagWithEdition from '../../atoms/TagWithEdition/TagWithEdition';
import AccountEdition from './AccountEdition/AccountEdition';
import Tag from '../../atoms/Tag/Tag';
import adminTypes from '../../../constants/adminTypes';

const scopes = {
  canUpdate: {
    platform: [SCOPE.update_pla_adminAccount],
    establishment: [SCOPE.update_ownEst_adminAccount],
  },
  canChangeStatus: {
    platform: [SCOPE.activate_pla_adminAccount, SCOPE.deactivate_pla_adminAccount],
    establishment: [SCOPE.activate_ownEst_adminAccount, SCOPE.deactivate_ownEst_adminAccount],
  },
};

function AdministrationTable({
  tableType, administrators, setAdministrators, roles, isFetching,
}) {
  const { t } = useTranslation();
  const { displayNotification } = useNotification();
  const { displayModal } = useModal();
  const [{ user }] = useAppContext();

  const { postUpdateStatus } = useAdministratorsService();

  const accountEditionForm = useRef();

  const { mutate: statusMutate } = useMutation(
    ({ accountId, isActive }) => postUpdateStatus({ type: tableType, accountId, isActive }),
    {
      onMutate: (updated) => {
        setAdministrators((state) => state.map((admin) => {
          if (admin.id === updated.accountId) {
            return { ...admin, isActive: updated.isActive };
          }
          return admin;
        }));
      },
      onError: (_, updated) => {
        setAdministrators((state) => state.map((admin) => {
          if (admin.id === updated.accountId) {
            return { ...admin, isActive: !updated.isActive };
          }
          return admin;
        }));
      },
      onSuccess: (_, updated) => {
        const admin = administrators?.find((a) => a.id === updated.accountId);
        if (admin) {
          displayNotification({
            message: t('admin.table.actions.status.success', { user: `${admin.firstName} ${admin.lastName}` }),
          });
        }
      },
    },
  );

  const columns = {
    name: {
      key: 'name', title: t('admin.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    email: { key: 'email', title: t('admin.table.columns.email') },
    role: { key: 'role', title: t('admin.table.columns.role') },
    status: { key: 'status', title: t('admin.table.columns.status') },
    actions: { key: 'actions', title: t('admin.table.columns.actions') },
  };

  const getColumnsByScope = () => {
    const columnsByScope = [columns.name, columns.email, columns.role, columns.status];

    // can update
    if (isGranted(user, scopes.canUpdate[tableType] || [])) columnsByScope.push(columns.actions);

    return columnsByScope;
  };

  const getAccountRow = (admin, index) => ({
    id: index,
    name: `${admin.lastName} ${admin.firstName}`,
    email: admin.emailAddress,
    role: roles?.find((r) => r.id === admin.roleId)?.name || t('common.undefined'),
    status: (
      isGranted(user, scopes.canChangeStatus[tableType] || []) ? (
        <TagWithEdition
          theme={admin.isActive ? 'green' : 'yellow'}
          label={t(`tables.status.${admin.isActive ? 'active' : 'inactive'}`)}
          options={[
            { value: 'active', label: t('tables.status.active') },
            { value: 'inactive', label: t('tables.status.inactive') },
          ]}
          onChange={(status) => {
            statusMutate({ accountId: admin.id, isActive: status === 'active' });
          }}
          activeValue={admin.isActive ? 'active' : 'inactive'}
        />
      ) : (
        <Tag
          theme={admin.isActive ? 'green' : 'yellow'}
          label={t(`tables.status.${admin.isActive ? 'active' : 'inactive'}`)}
        />
      )
    ),
    actions: (
      <PermissionsGate scopes={scopes.canUpdate[tableType] || []}>
        <button
          type="button"
          onClick={() => displayModal({
            maxWidth: 455,
            title: t('admin.edition.title'),
            content: (
              <AccountEdition
                tableAdminType={tableType}
                account={admin}
                ref={accountEditionForm}
                roles={roles}
                setAdministrators={setAdministrators}
              />
            ),
            buttons: [
              {
                label: t('common.cancel'),
                theme: 'inverted',
              },
              {
                label: t('admin.edition.form.submit'),
                onClick: () => accountEditionForm?.current?.submitForm(),
                queryName: 'putAccount',
              },
            ],
          })}
          className="d-flex a-center c-primary-500 fs-16 font-secondary fw-600 c-pointer"
        >
          <EditIcon className="m-r-10" width={14} height={14} />
          {t('admin.table.actions.edit')}
        </button>
      </PermissionsGate>
    ),
  });

  return (
    (isFetching) ? (
      <>
        <SkeletonLoader height={60} className="m-b-20" />
        <SkeletonLoader height={300} />
      </>
    ) : (
      administrators?.length ? (
        <GenericTable
          columns={getColumnsByScope()}
          rows={administrators.map((admin, index) => getAccountRow(admin, index))}
          isSearchable
          searchPlaceholder={t('admin.table.search')}
        />
      ) : (
        <EmptyList
          imageTheme="users"
          title={t('admin.table.empty.title')}
          text={
            `${t('admin.table.empty.text')} ${isGranted(
              user,
              tableType === adminTypes.platform ? [
                SCOPE.create_pla_adminAccount,
              ] : [
                SCOPE.create_est_adminAccount,
                SCOPE.create_ownEst_adminAccount,
              ],
            )
              ? t('admin.table.empty.add')
              : ''}`
          }
        />
      )
    )
  );
}

AdministrationTable.propTypes = {
  tableType: PropTypes.string.isRequired,
  administrators: PropTypes.arrayOf(PropTypes.shape({})),
  setAdministrators: PropTypes.func,
  roles: PropTypes.arrayOf(PropTypes.shape({})),
  isFetching: PropTypes.bool,
};

AdministrationTable.defaultProps = {
  administrators: [],
  setAdministrators: () => {},
  roles: [],
  isFetching: false,
};

export default AdministrationTable;
